import React from 'react';
import { Button } from './ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { useOrderStore } from '../store/orderStore';

import { parseProductSize, findSmallestFittingVariant } from '../utils/productSizeParser';
import { Input } from './ui/input';

// SizeSelectorコンポーネント
interface SizeSelectorProps {
  value?: string;
  onValueChange?: (value: string) => void;
  className?: string;
  optionIds?: {
    deliverySize: string;
    width: string;
    height: string;
  };
}

const SizeSelector: React.FC<SizeSelectorProps> = ({
  value,
  onValueChange,
  className = '',
  optionIds
}) => {
  const {
    selectedSize,
    setSize,
    customPaperWidth,
    customPaperHeight,
    setCustomPaperDimensions,
    selectedProduct,
    deliverySize,
    setDeliverySize,
    setProductOption,
    productOptions
  } = useOrderStore();

  // 初期化: deliverySizeが未設定の場合は'定形サイズ'に設定
  React.useEffect(() => {
    if (!deliverySize) {
      console.log('OptionSelectors: Initializing deliverySize to 定形サイズ');
      setDeliverySize('定形サイズ');
      if (optionIds) {
        setProductOption(optionIds.deliverySize, '定形サイズ');
      }
    }
  }, [deliverySize, setDeliverySize, optionIds, setProductOption]);

  // productOptionsからの初期化
  React.useEffect(() => {
    if (optionIds && productOptions[optionIds.deliverySize]) {
      const storedDeliverySize = productOptions[optionIds.deliverySize];
      if (storedDeliverySize !== deliverySize) {
        console.log('OptionSelectors: Syncing deliverySize from productOptions', storedDeliverySize);
        setDeliverySize(storedDeliverySize as any);
      }
    }
  }, [optionIds, productOptions, deliverySize, setDeliverySize]);

  const handleDeliverySizeChange = (size: '定形サイズ' | '定形外サイズ') => {
    console.log('OptionSelectors: handleDeliverySizeChange', size);
    setDeliverySize(size);
    if (optionIds) {
      setProductOption(optionIds.deliverySize, size);
    }
  };

  const handleSizeChange = (newValue: string) => {
    console.log('handleSizeChange called with:', newValue);
    // コールバック関数を呼び出し（既存の機能）
    onValueChange?.(newValue);
    // orderStoreに保存
    setSize(newValue);

    // 定形サイズの場合のみ、選択されたサイズに基づいてカスタム寸法を更新
    // 定形外サイズの場合は、入力された寸法を維持する
    if (deliverySize === '定形サイズ' && selectedProduct?.variants) {
      const variant = selectedProduct.variants.find(v => String(v.id) === newValue);
      if (variant) {
        const size = parseProductSize(variant.title);
        if (size) {
          setCustomPaperDimensions(size.shortEdge, size.longEdge);
          // productOptionsも更新（定形サイズの場合でも寸法を入れておく要件があれば）
          // 今回は定形外サイズの場合のみ寸法入力が有効なので、ここでは更新しない、またはクリアする
          if (optionIds) {
            // 定形サイズ選択時は寸法をクリアするか、あるいは自動入力するか。
            // Print.jsonの定義では定形外サイズの場合のみ表示されるため、裏で値を持っていても問題ないが、
            // 念のためクリアしておくのが安全かもしれない。
            // setProductOption(optionIds.width, '');
            // setProductOption(optionIds.height, '');
          }
        }
      }
    }
  };

  const handleCustomDimensionChange = (width: number | null, height: number | null) => {
    console.log('handleCustomDimensionChange:', { width, height });
    setCustomPaperDimensions(width, height);

    if (optionIds) {
      setProductOption(optionIds.width, width);
      setProductOption(optionIds.height, height);
    }

    if (width && height && selectedProduct?.variants) {
      // 入力された寸法が収まる最小の用紙サイズを検索
      const fittingVariant = findSmallestFittingVariant(width, height, selectedProduct.variants);

      if (fittingVariant && String(fittingVariant.id) !== selectedSize) {
        const newSizeId = String(fittingVariant.id);
        setSize(newSizeId);
        onValueChange?.(newSizeId);
      }
    }
  };

  // 選択肢の生成
  // selectedProductがない場合は空配列
  const sizeOptions = selectedProduct?.variants.map(variant => ({
    value: String(variant.id),
    label: variant.title
  })) || [];

  const isStandard = deliverySize === '定形サイズ';
  console.log('OptionSelectors: Render - deliverySize:', deliverySize, 'isStandard:', isStandard);

  return (
    <div className={`space-y-4 ${className}`}>
      {/* 定形・定形外切り替え */}
      <div className="flex space-x-2 p-1 bg-gray-100 rounded-lg">
        <button
          className={`flex-1 py-1.5 text-sm font-medium rounded-md transition-colors ${isStandard
            ? 'bg-white text-gray-900 shadow-sm'
            : 'text-gray-500 hover:text-gray-900'
            }`}
          onClick={() => handleDeliverySizeChange('定形サイズ')}
        >
          定形サイズ
        </button>
        <button
          className={`flex-1 py-1.5 text-sm font-medium rounded-md transition-colors ${!isStandard
            ? 'bg-white text-gray-900 shadow-sm'
            : 'text-gray-500 hover:text-gray-900'
            }`}
          onClick={() => handleDeliverySizeChange('定形外サイズ')}
        >
          定形外サイズ
        </button>
      </div>

      <div className="space-y-2">
        <label className="text-sm font-medium text-gray-700">納品サイズ</label>
        <Select
          value={value || selectedSize}
          onValueChange={handleSizeChange}
          disabled={!isStandard} // 定形外の場合は無効化（または読み取り専用）
        >
          <SelectTrigger className={`w-full ${!isStandard ? 'bg-gray-100 text-gray-500' : ''}`}>
            <SelectValue placeholder="サイズを選択してください" />
          </SelectTrigger>
          <SelectContent>
            {sizeOptions.length > 0 ? (
              sizeOptions.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))
            ) : (
              <SelectItem value="no-options" disabled>
                商品を選択してください
              </SelectItem>
            )}
          </SelectContent>
        </Select>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <label className="text-sm font-medium text-gray-700">短辺 (mm)</label>
          <Input
            type="number"
            value={customPaperWidth || ''}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              const val = e.target.value ? Number(e.target.value) : null;
              handleCustomDimensionChange(val, customPaperHeight);
            }}
            placeholder="幅"
            readOnly={isStandard} // 定形サイズの場合は読み取り専用
            className={isStandard ? 'bg-gray-100 text-gray-500' : ''}
          />
        </div>
        <div className="space-y-2">
          <label className="text-sm font-medium text-gray-700">長辺 (mm)</label>
          <Input
            type="number"
            value={customPaperHeight || ''}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              const val = e.target.value ? Number(e.target.value) : null;
              handleCustomDimensionChange(customPaperWidth, val);
            }}
            placeholder="高さ"
            readOnly={isStandard} // 定形サイズの場合は読み取り専用
            className={isStandard ? 'bg-gray-100 text-gray-500' : ''}
          />
        </div>
      </div>
      {!isStandard && (
        <div className="mt-2 text-xs text-gray-500 space-y-1">
          <p>※はじめにご希望の納品サイズの短辺＆長辺が収まる「用紙サイズ」をご選択の上で、納品サイズのご指定をお願いいたします。</p>
          <p>※1枚をカットして2枚の納品になる場合は、納品枚数分の料金を頂戴しております。</p>
        </div>
      )}
    </div>
  );
};

// FrameSelectorコンポーネント
interface FrameSelectorProps {
  value?: string;
  onValueChange?: (value: string) => void;
  className?: string;
}

const FrameSelector: React.FC<FrameSelectorProps> = ({
  value,
  onValueChange,
  className = ''
}) => {
  const { selectedFrame, setFrame } = useOrderStore();

  const handleValueChange = (newValue: string) => {
    // コールバック関数を呼び出し（既存の機能）
    onValueChange?.(newValue);
    // orderStoreに保存
    setFrame(newValue);
  };

  const frameOptions = [
    { value: 'no-frame', label: 'No Frame', color: 'bg-gray-100 hover:bg-gray-200' },
    { value: 'black', label: 'Black', color: 'bg-black hover:bg-gray-800 text-white' },
    { value: 'white', label: 'White', color: 'bg-white hover:bg-gray-100 border border-gray-300' },
    { value: 'natural', label: 'Natural', color: 'bg-amber-100 hover:bg-amber-200 border border-amber-300' },
  ];

  const currentValue = value || selectedFrame;

  return (
    <div className={`space-y-2 ${className}`}>
      <label className="text-sm font-medium text-gray-700">フレーム</label>
      <div className="grid grid-cols-2 gap-2">
        {frameOptions.map((option) => (
          <Button
            key={option.value}
            variant={currentValue === option.value ? "default" : "outline"}
            className={`${option.color} ${currentValue === option.value
              ? 'ring-2 ring-blue-500 ring-offset-2'
              : ''
              }`}
            onClick={() => handleValueChange(option.value)}
          >
            {option.label}
          </Button>
        ))}
      </div>
    </div>
  );
};

// MatSelectorコンポーネント
interface MatSelectorProps {
  value?: string;
  onValueChange?: (value: string) => void;
  className?: string;
}

const MatSelector: React.FC<MatSelectorProps> = ({
  value,
  onValueChange,
  className = ''
}) => {
  const { selectedMat, setMat } = useOrderStore();

  const handleValueChange = (newValue: string) => {
    // コールバック関数を呼び出し（既存の機能）
    onValueChange?.(newValue);
    // orderStoreに保存
    setMat(newValue);
  };

  const matOptions = [
    { value: 'no-mat', label: 'No Mat', color: 'bg-gray-100 hover:bg-gray-200' },
    { value: 'mat', label: 'Mat', color: 'bg-white hover:bg-gray-50 border border-gray-300' },
  ];

  const currentValue = value || selectedMat;

  return (
    <div className={`space-y-2 ${className}`}>
      <label className="text-sm font-medium text-gray-700">マット</label>
      <div className="grid grid-cols-2 gap-2">
        {matOptions.map((option) => (
          <Button
            key={option.value}
            variant={currentValue === option.value ? "default" : "outline"}
            className={`${option.color} ${currentValue === option.value
              ? 'ring-2 ring-blue-500 ring-offset-2'
              : ''
              }`}
            onClick={() => handleValueChange(option.value)}
          >
            {option.label}
          </Button>
        ))}
      </div>
    </div>
  );
};

// メインのOptionSelectorsコンポーネント
interface OptionSelectorsProps {
  sizeValue?: string;
  onSizeChange?: (value: string) => void;
  frameValue?: string;
  onFrameChange?: (value: string) => void;
  matValue?: string;
  onMatChange?: (value: string) => void;
  className?: string;
}

const OptionSelectors: React.FC<OptionSelectorsProps> = ({
  sizeValue,
  onSizeChange,
  frameValue,
  onFrameChange,
  matValue,
  onMatChange,
  className = ''
}) => {
  return (
    <div className={`space-y-6 ${className}`}>
      <SizeSelector
        value={sizeValue}
        onValueChange={onSizeChange}
      />

      <FrameSelector
        value={frameValue}
        onValueChange={onFrameChange}
      />

      <MatSelector
        value={matValue}
        onValueChange={onMatChange}
      />
    </div>
  );
};

// 個別のコンポーネントもエクスポート
export { SizeSelector, FrameSelector, MatSelector };
export default OptionSelectors;
